import React from 'react';
import { useIntl } from 'react-intl';
import { StreamingEarningsSummary } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { formatChangePct, formatMonth } from '../streamingFormat';
import { earningsSources, sourceNames } from '../streamingSources';
import { streamingTotalsMessages as messages } from '../streamingTotalsMessages';
import { MoneyAmount } from './MoneyAmount';
import { SourceMark } from './SourceMark';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingEarningsTileProps {
  earnings: StreamingEarningsSummary;
}

/**
 * The Net earnings tile (design a1). One amount for each currency, one under the other: two
 * currencies are never added. The change shows only when the period is long enough to be
 * reliable (owner answer 2026-10-06), and a note says when the newest weeks can still grow.
 * The parent does not draw this tile for a caller who may not see money.
 */
const StreamingEarningsTileComponent: React.FC<StreamingEarningsTileProps> = ({ earnings }) => {
  const intl = useIntl();
  const amounts = earnings.amounts ?? [];
  const sources = earningsSources(earnings);
  const names = sourceNames(sources, 'shortName');
  const label = intl.formatMessage(messages.netEarnings);

  return (
    <Panel className={styles.tile} role="group" aria-label={label} data-testid="streaming-net-earnings">
      <span className={styles.tileLabel}>{label}</span>
      {amounts.length === 0 ? (
        <span className={`${styles.tileValue} ${styles.tileValueFaint}`}>—</span>
      ) : (
        amounts.map((amount) => {
          const change = earnings.changeReliable ? formatChangePct(intl, amount.changePct) : null;
          return (
            <span key={amount.currency ?? 'none'} className={styles.tileRow}>
              <MoneyAmount value={amount.net} currency={amount.currency} className={styles.tileValue} />
              {change !== null && amount.changePct !== null ? (
                <DeltaPill value={amount.changePct} label={change} />
              ) : null}
            </span>
          );
        })
      )}
      <span className={styles.tileFrom}>
        {sources.map((type) => (
          <SourceMark key={type} integrationType={type} />
        ))}
        {earnings.months === null
          ? names
          : intl.formatMessage(messages.fromEarnings, {
              source: names,
              start: formatMonth(intl, earnings.months.from),
              end: formatMonth(intl, earnings.months.to),
            })}
      </span>
      {amounts.length === 0 ? <p className={styles.footnote}>{intl.formatMessage(messages.noEarnings)}</p> : null}
      {amounts.some((amount) => amount.currency === null) ? (
        <p className={styles.footnote}>{intl.formatMessage(messages.noCurrency)}</p>
      ) : null}
      {amounts.length > 0 && !earnings.changeReliable ? (
        <p className={styles.footnote}>{intl.formatMessage(messages.changeNotReliable)}</p>
      ) : null}
      {earnings.mayGrow ? <p className={styles.footnote}>{intl.formatMessage(messages.mayGrow)}</p> : null}
    </Panel>
  );
};

export const StreamingEarningsTile = TypedMemo(StreamingEarningsTileComponent);
